import { useMemo, useState } from "react";
import { Button, Col, Form, Row, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import useApi from "../../Hooks/useApi";
import * as postapi from "../../api/postapi";

const parseCsvLine = (line) => {
  // Minimal CSV parsing: supports quoted values, commas.
  const result = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];

    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === "," && !inQuotes) {
      result.push(current);
      current = "";
    } else {
      current += char;
    }
  }

  result.push(current);
  return result.map((v) => String(v).trim());
};

const normalizeRow = (row) => {
  const fk_listing_id = row.fk_listing_id != null && String(row.fk_listing_id).length ? Number(row.fk_listing_id) : null;
  const rating = row.rating != null && String(row.rating).length ? Number(row.rating) : null;
  const is_pending = row.is_pending != null && String(row.is_pending).length ? Number(row.is_pending) : 0;
  const review = row.review != null ? String(row.review) : "";

  const errors = [];
  if (!Number.isFinite(fk_listing_id) || fk_listing_id <= 0) errors.push("fk_listing_id is required");
  if (rating === null) errors.push("rating is required");
  if (rating !== null && (!Number.isFinite(rating) || rating < 1 || rating > 5)) errors.push("rating must be 1..5");
  if (String(review).trim().length === 0) errors.push("review is required");
  if (![0, 1].includes(is_pending)) errors.push("is_pending must be 0 or 1");

  return {
    fk_listing_id,
    rating,
    review,
    is_pending,
    errors,
  };
};

const ListingReviewsDummyBulkUploadModal = ({ onSuccess, onCancel }) => {
  const [file, setFile] = useState(null);
  const [rawText, setRawText] = useState("");
  const [rows, setRows] = useState([]);
  const [lastUploadSummary, setLastUploadSummary] = useState(null);
  const [lastUploadFailures, setLastUploadFailures] = useState([]);

  const downloadSampleCsv = () => {
    const content =
      "fk_listing_id,rating,review,is_pending\n" +
      "31564,5,\"Excellent quality, exactly as described.\",0\n" +
      "31564,4,\"Good value for money. Delivery was quick.\",0\n" +
      "88210,3,\"Average experience. Product is okay but packaging could be better.\",1\n";
    const blob = new Blob([content], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "dummy_reviews_upload_sample.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const bulkCreateDummyListingReviews = useApi(postapi.bulkCreateDummyListingReviews, { hasCatchError: true });

  const parsedRows = useMemo(() => {
    return rows.map((r) => normalizeRow(r));
  }, [rows]);

  const validRows = useMemo(() => parsedRows.filter((r) => r.errors.length === 0), [parsedRows]);
  const invalidRows = useMemo(() => parsedRows.filter((r) => r.errors.length > 0), [parsedRows]);

  const parseInput = (text, mime) => {
    const trimmed = String(text || "").trim();
    if (!trimmed.length) {
      setRows([]);
      return;
    }

    // JSON
    if (mime?.includes("json") || trimmed.startsWith("[") || trimmed.startsWith("{")) {
      const data = JSON.parse(trimmed);
      const arr = Array.isArray(data) ? data : data?.rows;
      if (!Array.isArray(arr)) throw new Error("JSON must be an array of rows or { rows: [...] }");
      setRows(arr);
      return;
    }

    // CSV
    const lines = trimmed.split(/\r?\n/).filter(Boolean);
    if (!lines.length) {
      setRows([]);
      return;
    }

    const header = parseCsvLine(lines[0]).map((h) => h.trim());
    const out = [];
    for (let i = 1; i < lines.length; i++) {
      const cols = parseCsvLine(lines[i]);
      const obj = {};
      header.forEach((key, idx) => {
        obj[key] = cols[idx] ?? "";
      });
      out.push(obj);
    }
    setRows(out);
  };

  const onFileChange = async (e) => {
    const f = e.target.files?.[0] || null;
    setFile(f);
    setRows([]);
    setLastUploadSummary(null);
    setLastUploadFailures([]);

    if (!f) return;

    const text = await f.text();
    setRawText(text);

    try {
      parseInput(text, f.type);
    } catch (err) {
      setRows([]);
      toast.error(err?.message || "Failed to parse file", { position: "bottom-right", autoClose: 2000 });
    }
  };

  const onSubmit = async () => {
    if (!validRows.length) {
      toast.error("No valid rows to upload", { position: "bottom-right", autoClose: 2000 });
      return;
    }

    try {
      const res = await bulkCreateDummyListingReviews.request({
        reviews: validRows.map((r) => ({
          fk_listing_id: r.fk_listing_id,
          rating: r.rating,
          review: String(r.review || "").trim(),
          is_pending: r.is_pending,
        })),
      });

      const results = res?.data?.data?.results || [];
      const summary = res?.data?.data?.summary || null;
      const failures = Array.isArray(results) ? results.filter((r) => r?.success === false) : [];

      setLastUploadSummary(summary);
      setLastUploadFailures(failures);

      if (summary) {
        toast.success(`Inserted ${summary.inserted}, Failed ${summary.failed}`, { position: "bottom-right", autoClose: 2000 });
      } else {
        toast.success("Bulk upload completed", { position: "bottom-right", autoClose: 1500 });
      }

      // Close modal only if everything succeeded
      if (summary && summary.failed === 0) {
        onSuccess();
      }
    } catch (err) {
      toast.error(err?.response?.data?.error?.message || "Bulk upload failed", { position: "bottom-right", autoClose: 2000 });
    }
  };

  return (
    <>
      <details className="mb-2">
        <summary style={{ cursor: "pointer" }}>Format help</summary>
        <div className="text-muted" style={{ fontSize: "12px", marginTop: "6px" }}>
          CSV headers: <code>fk_listing_id</code>, <code>rating</code>, <code>review</code>, <code>is_pending</code> (0/1, optional)
          <br />
          JSON: <code>{`[{"fk_listing_id":31564,"rating":5,"review":"...","is_pending":0}, ...]`}</code>
        </div>
      </details>

      <Row className="g-2 align-items-end mb-2">
        <Col>
          <Form.Group>
            <Form.Label className="mb-1">Upload file</Form.Label>
            <Form.Control type="file" accept=".csv,.json" onChange={onFileChange} />
          </Form.Group>
        </Col>
        <Col xs="12" lg="4">
          <Button variant="outline-secondary" className="w-100" type="button" onClick={downloadSampleCsv}>
            Download sample CSV
          </Button>
        </Col>
      </Row>

      {(rows.length > 0 || lastUploadSummary) && (
        <div className="mb-2" style={{ fontSize: "13px" }}>
          {rows.length > 0 && (
            <span>
              Rows: <b>{rows.length}</b> | Valid: <b>{validRows.length}</b> | Invalid: <b>{invalidRows.length}</b>
            </span>
          )}
          {rows.length > 0 && lastUploadSummary && <span className="mx-2">|</span>}
          {lastUploadSummary && (
            <span>
              Inserted: <b>{lastUploadSummary.inserted}</b> | Failed: <b>{lastUploadSummary.failed}</b>
            </span>
          )}
        </div>
      )}

      {lastUploadFailures?.length > 0 && (
        <div style={{ maxHeight: "22vh", overflow: "auto" }} className="mb-2">
          <div className="mb-1" style={{ fontSize: "13px" }}>
            <b>Failed rows</b>
          </div>
          <Table bordered size="sm" responsive className="mb-0">
            <thead>
              <tr>
                <th>index</th>
                <th>message</th>
              </tr>
            </thead>
            <tbody>
              {lastUploadFailures.slice(0, 200).map((r, idx) => (
                <tr key={idx}>
                  <td>{String(r?.index ?? "")}</td>
                  <td className="text-danger">{String(r?.message || "Failed")}</td>
                </tr>
              ))}
            </tbody>
          </Table>
          {lastUploadFailures.length > 200 && <div className="text-muted">Showing first 200 failures</div>}
        </div>
      )}

      {rows.length > 0 && (
        <div style={{ maxHeight: "40vh", overflow: "auto" }} className="mb-2">
          <Table striped responsive size="sm" className="mb-0">
            <thead>
              <tr>
                <th>fk_listing_id</th>
                <th>rating</th>
                <th>is_pending</th>
                <th>review</th>
                <th>errors</th>
              </tr>
            </thead>
            <tbody>
              {parsedRows.slice(0, 200).map((r, idx) => (
                <tr key={idx}>
                  <td>{String(r.fk_listing_id ?? "")}</td>
                  <td>{String(r.rating ?? "")}</td>
                  <td>{String(r.is_pending ?? "")}</td>
                  <td style={{ maxWidth: 250, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.review}</td>
                  <td className={r.errors.length ? "text-danger" : "text-success"}>
                    {r.errors.length ? r.errors.join(", ") : "OK"}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          {parsedRows.length > 200 && <div className="text-muted">Showing first 200 rows preview</div>}
        </div>
      )}

      <Row className="mt-2">
        <Col>
          <Button variant="outline-secondary" className="w-100" type="button" onClick={onCancel}>
            Cancel
          </Button>
        </Col>
        <Col>
          <Button className="w-100" type="button" disabled={!validRows.length} onClick={onSubmit}>
            Upload {validRows.length ? `(${validRows.length})` : ""}
          </Button>
        </Col>
      </Row>
    </>
  );
};

export default ListingReviewsDummyBulkUploadModal;
